import React, { useState, useEffect } from 'react';
import { Box, Chip, Snackbar, Alert } from '@mui/material';
import { CloudOff, CloudDone, Sync } from '@mui/icons-material';
import offlinePaymentService from '../../services/offlinePaymentService';

const OnlineStatusIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncMessage, setSyncMessage] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const updateOnlineStatus = async () => {
      const online = navigator.onLine;
      setIsOnline(online);
      
      if (online) {
        // Auto-sync when coming online (with small delay)
        setTimeout(() => {
          handleSync();
        }, 1000);
      }
      
      // Update pending count
      const count = await offlinePaymentService.getPendingPaymentsCount();
      setPendingCount(count);
    };

    // Initial check
    updateOnlineStatus();

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    // Check pending payments periodically
    const checkPending = async () => {
      try {
        const count = await offlinePaymentService.getPendingPaymentsCount();
        setPendingCount(count);
      } catch (error) {
        console.error('Error checking pending payments:', error);
      }
    };

    // Listen for payment updates
    const handlePaymentUpdate = () => {
      checkPending();
    };

    window.addEventListener('offlinePaymentUpdated', handlePaymentUpdate);
    const interval = setInterval(checkPending, 3000); // Check every 3 seconds

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      window.removeEventListener('offlinePaymentUpdated', handlePaymentUpdate);
      clearInterval(interval);
    };
  }, []);

  const handleSync = async () => {
    const online = navigator.onLine;
    if (syncing || !online) {
      if (!online) {
        setSyncMessage({
          type: 'error',
          text: 'Device is offline. Cannot sync.',
        });
      }
      return;
    }

    setSyncing(true);
    try {
      const result = await offlinePaymentService.manualSync();
      
      if (result.success) {
        setSyncMessage({
          type: 'success',
          text: result.message || `Successfully synced ${result.synced || 0} payment(s)`,
        });
      } else {
        setSyncMessage({
          type: 'error',
          text: result.message || 'Failed to sync payments',
        });
      }
      
      // Update pending count
      const count = await offlinePaymentService.getPendingPaymentsCount();
      setPendingCount(count);
    } catch (error) {
      console.error('Error during sync:', error);
      setSyncMessage({
        type: 'error',
        text: 'Error syncing payments. Please try again.',
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
      <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 1000 }}>
        <Chip
          icon={isOnline ? <CloudDone /> : <CloudOff />}
          label={
            isOnline
              ? pendingCount > 0
                ? `Online (${pendingCount} pending)`
                : 'Online'
              : 'Offline'
          }
          color={isOnline ? (pendingCount > 0 ? 'warning' : 'success') : 'error'}
          onClick={isOnline && pendingCount > 0 ? handleSync : undefined}
          sx={{ cursor: isOnline && pendingCount > 0 ? 'pointer' : 'default' }}
        />
        {syncing && (
          <Chip
            icon={<Sync />}
            label="Syncing..."
            color="info"
            sx={{ ml: 1 }}
          />
        )}
      </Box>

      <Snackbar
        open={!!syncMessage}
        autoHideDuration={6000}
        onClose={() => setSyncMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSyncMessage(null)}
          severity={syncMessage?.type || 'info'}
          sx={{ width: '100%' }}
        >
          {syncMessage?.text}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OnlineStatusIndicator;






